import { Component, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { MatSnackBar } from '@angular/material/snack-bar'
import { MatTableDataSource } from '@angular/material/table'
import { ActivatedRoute, Router } from '@angular/router'
import {
  ApiService,
  AppointmentDataService,
  AppointmentModel,
  CrudService,
  EligibilityBenefits,
  EligibilityNavigationSeed,
  Estimate,
  EstimateData, EstimateModel,
  EstimateService,
  FacilityService,
  Invoice,
  InvoiceLineItem,
  isValidId,
  ParamsService,
  Patient,
  PatientDataService,
  PatientInsurance,
  ShopperEstimate,
  ShopperEstimateService,
  ShopperEstimateViewFields,
} from '@mmx/shared'
import { isArray } from 'lodash'
import { asyncScheduler, BehaviorSubject, combineLatest, defer, merge, Observable, of, ReplaySubject, Subject } from 'rxjs'
import { catchError, combineLatestWith, distinctUntilChanged, filter, map, observeOn, startWith, switchMap, takeUntil, tap } from 'rxjs/operators'

import { EstimateSummaryComponent } from './estimate-summary/estimate-summary.component'
import { ShopperInsuranceChangeEvent } from './shopper-insurance-card/shopper-insurance-card.component'

export type ShopperEstimateViewModel = EstimateModel & ShopperEstimateViewFields

@Component({
  selector: 'mmx-estimate',
  templateUrl: './estimate.component.html',
  styleUrls: ['./estimate.component.scss'],
  standalone: false,
})
export class EstimateComponent implements OnInit, OnDestroy {
  @ViewChild(EstimateSummaryComponent) estimateSummary?: EstimateSummaryComponent
  @Input() embedded = false
  @Input() shopper = false

  private readonly facilityService = inject(FacilityService)
  private readonly shopperEstimateService = inject(ShopperEstimateService)

  get shopperForm() {
    return this.shopperEstimateService.shopperForm
  }

  /**
   * estimateId input only used in embedded app,
   * input can be ignored otherwise
   */
  @Input()
  set estimateId(value: string | undefined) {
    this.embeddedEstimateId$.next(value)
  }
  public patient: Patient
  public insurances: PatientInsurance[]
  public estimate: ShopperEstimateViewModel
  public isPrinting = new BehaviorSubject<boolean>(false)
  public ESTIMATE_STATUS = Estimate.STATUS

  // Guards the reseed below from firing on every viewModel$ replay (insurances$/
  // allAppointments$/etc. can all re-emit mid-session) - only a genuinely different estimate
  // should reset what counts as "the insurance this slot's fields were last synced against".
  private lastLoadedEstimateId: string | undefined
  private lastLoadedSavedEstimate: EstimateModel | undefined
  // Insurance id benefits were last synced for, per slot - used only to tell a genuine live
  // insurance switch (which should always apply the new insurance's benefits) apart from
  // everything else (which should respect a per-field manual override). See
  // resolveBenefitField().
  public lastAppliedPrimaryInsuranceId: string | null | undefined
  public lastAppliedSecondaryInsuranceId: string | null | undefined

  /**
   * If this invoice is not editable and assigned to an appointment,
   * this will hold the appointment information so we can add it to the breadcrumbs
   */
  public appointment: AppointmentModel
  public allAppointments: AppointmentModel[] = []
  public items: MatTableDataSource<InvoiceLineItem>
  public STATUS = Invoice.STATUS
  public PATIENT_TYPE = Estimate.PATIENT_TYPE

  get showEstimateInputs(): boolean {
    const estimate = this.estimateService.estimate$.getValue()
    return estimate?.status === this.ESTIMATE_STATUS.DRAFT || (this.shopper && !estimate?.id)
  }

  get hasShopperInsurance(): boolean {
    return !!this.estimate?.shopperInsurances?.P
  }

  private onDestroy$ = new Subject<void>()
  private embeddedEstimateId$ = new ReplaySubject<string | undefined>(1)
  private standardEstimateId$ = new Subject<string | undefined>()
  private estimateId$ = merge(
    this.route.params.pipe(map(p => ({ id: p?.id as string | undefined, explicit: false }))),
    // Explicit pushes (e.g. resetting in place after voiding an embedded shopper estimate)
    // must always re-run the pipeline, even when the value is already 'new'. In the routeless
    // embedded widget the id never leaves 'new', which previously swallowed the reset push
    // entirely (PIA-2694).
    this.standardEstimateId$.pipe(map(id => ({ id, explicit: true }))),
    this.embeddedEstimateId$.pipe(
      filter(() => this.embedded),
      map(id => ({ id, explicit: false })),
    ),
  ).pipe(
    map(({ id, explicit }) => ({ id: id ?? 'new', explicit })),
    // Passive sources dedupe against whatever ran last, so echoes of an explicit reset
    // (e.g. the post-delete appointment refetch unbinding the estimateId input) don't
    // re-run the pipeline a second time.
    distinctUntilChanged((prev, curr) => !curr.explicit && prev.id === curr.id),
    map(({ id }) => id),
  )

  public viewModel$ = combineLatest([
    this.estimateId$.pipe(
      switchMap((estimateId): Observable<[EstimateModel, boolean]> => {
        // Ids arrive via route params, so treat malformed (non-UUID) ids like
        // unknown ones instead of letting them reach a request URL.
        if (!estimateId || estimateId === 'new' || !isValidId(estimateId)) {
          this.estimateService.resetState()
          return of<[EstimateModel, boolean]>([new EstimateModel(), true])
        }

        this.estimateLoading$.next(true)
        this.lastAppliedPrimaryInsuranceId = null
        this.lastAppliedSecondaryInsuranceId = null
        this.estimateService.primaryInsurance$.next(undefined)
        this.estimateService.secondaryInsurance$.next(undefined)

        if (this.shopper) {
          return this.shopperEstimateService.get(estimateId).pipe(
            map((shopperEstimate): [EstimateModel, boolean] => {
              const estimate: ShopperEstimateViewModel = new EstimateModel({
                id: shopperEstimate.id,
                facilityId: shopperEstimate.facilityId,
                patientId: null,
                appointmentIds: [],
                charges: shopperEstimate.charges,
                inputs: shopperEstimate.inputs,
                output: shopperEstimate.output,
                status: shopperEstimate.status === ShopperEstimate.STATUS.FINALIZED
                  ? Estimate.STATUS.CONFIRMED
                  : Estimate.STATUS.DRAFT,
              })
              estimate.eligibilityId = shopperEstimate.eligibilityId
              estimate.shopperInsurances = shopperEstimate.insurances
              estimate.patientEmail = shopperEstimate.patientEmail
              estimate.patientPhone = shopperEstimate.patientPhone

              this.shopperForm.patchValue({
                patientFirstName: shopperEstimate.patientFirstName ?? '',
                patientLastName: shopperEstimate.patientLastName ?? '',
                patientDOB: shopperEstimate.patientDOB ?? '',
                patientEmail: shopperEstimate.patientEmail ?? null,
                patientPhone: shopperEstimate.patientPhone ?? null,
              })

              return [estimate, false]
            }),
            catchError((error) => {
              if (error.status === 404) {
                return of<[EstimateModel, boolean]>([new EstimateModel(), true])
              }
              throw error
            }),
          )
        }

        return this.crudService.read(EstimateModel, estimateId)
          .pipe(
            map((estimate): [EstimateModel, boolean] => [estimate, false]),
            catchError((error) => {
              if (error.status === 404) {
                return of<[EstimateModel, boolean]>([new EstimateModel(), true])
              }

              throw error
            }),

          )
      }),
    ),
    defer(() => this.shopper ? of(null) : this.patientDataService.patient$),
    defer(() => this.shopper ? of([]) : this.patientDataService.insurances$),
    defer(() => this.shopper ? of([]) : this.estimateService.allAppointments$),
    defer(() =>
      this.shopper
        ? of(null)
        : this.route.queryParams.pipe(
          combineLatestWith(
            this.paramsService.patientDefined$.pipe(
              filter(Boolean),
              distinctUntilChanged(),
            ),
          ),
          switchMap(([queryParams, patientId]) => {
            if (queryParams.episode) {
              return this.apiService
                .getAppointments({ patientId, episodeId: queryParams.episode })
                .pipe(map(page => page.data))
            } else if (queryParams.appointment) {
              return this.apiService.getAppointment(queryParams.appointment)
            } else {
              return of(null)
            }
          }),
        ),
    ),
    defer(() =>
      this.embedded
        ? this.appointmentDataService.appointment$.pipe(
          startWith(null),
          distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
        )
        : of(null),
    ),
  ]).pipe(
    observeOn(asyncScheduler),
    tap(([
      [estimate, isNew],
      patient,
      insurances,
      appointments,
      appointmentsSelected,
      activeAppointment,
    ]) => {
      this.patient = patient

      this.estimate = estimate

      const loadedEstimateId = estimate.id ?? 'new'
      if (this.lastLoadedEstimateId !== loadedEstimateId) {
        this.lastLoadedEstimateId = loadedEstimateId
        this.lastAppliedPrimaryInsuranceId = estimate.insurances?.P?.insuranceId ?? null
        this.lastAppliedSecondaryInsuranceId = estimate.insurances?.S?.insuranceId ?? null

        // Seed from the persisted snapshot immediately, before anything below can cause
        // primaryInsurance$/secondaryInsurance$ to emit and kick off a live benefits$ fetch,
        // so that fetch's previousOriginal is the real last-synced ground truth (not null) and
        // isModified()/resetToOriginal() in estimate-benefits.component.ts read correctly from
        // the moment this estimate renders, rather than flashing "unmodified" until the fetch
        // lands. Shopper mode below sets its own equivalents from shopperInsurances instead.
        // Gated on a genuine estimate-identity change (not every viewModel$ replay) so an
        // unrelated replay (e.g. an insurances$ refetch after an eligibility card save) can't
        // race the live benefits$ fetch that same save triggers and reset these back to the
        // stale persisted value (PIA-3167).
        this.estimateService.originalBenefits$.next(estimate.originalBenefits ?? null)
        this.estimateService.originalSecondaryBenefits$.next(estimate.originalSecondaryBenefits ?? null)
      }

      // viewModel$ replays reuse the fetched instance, so only a new fetch is announced.
      if (!isNew && estimate !== this.lastLoadedSavedEstimate) {
        this.lastLoadedSavedEstimate = estimate
        this.estimateService.savedEstimateLoaded$.next(this.estimateService.estimateModelToEstimateData(estimate))
      }

      let seed: EligibilityNavigationSeed | null = null
      if (this.shopper && isNew) {
        seed = this.shopperEstimateService.applyNavigationState()
        if (seed) {
          this.estimate.shopperInsurances = seed.shopperInsurances
          this.estimate.eligibilityId = seed.eligibilityId
          this.estimate.inputs = { ...this.estimate.inputs, ...seed.inputs }
        }
      }

      if (this.shopper) {
        // EstimateModel.toJSON() drops the shopper-only fields tacked onto the
        // model instance above, so on reload the send menu sees no email/phone
        // and disables itself. Push a plain object that preserves them.
        const shopperData: EstimateData & ShopperEstimateViewFields = {
          ...this.estimateService.estimateModelToEstimateData(estimate),
          shopperInsurances: this.estimate.shopperInsurances,
          eligibilityId: this.estimate.eligibilityId,
          patientEmail: this.estimate.patientEmail,
          patientPhone: this.estimate.patientPhone,
        }
        this.estimateService.setEstimate(shopperData)
      } else {
        this.estimateService.setEstimate(estimate)
      }
      this.estimateService.initializeEstimateForm(estimate, isNew)
      this.estimateLoading$.next(false)
      this.insurances = insurances
      this.allAppointments = appointments ?? []

      if (this.shopper) {
        // Shopper mode bypasses the PatientInsurance flow.
        this.estimateService.setPrimaryInsurance(null)
        this.estimateService.setSecondaryInsurance(null)
        this.estimateService.hasSecondaryInsurance$.next(false)

        const shopperIns = this.estimate.shopperInsurances
        if (seed?.benefits) {
          // Use the pulled benefits directly so ambiguity flags survive —
          // ShopperEstimateInsurance doesn't carry them.
          this.estimateService.originalBenefits$.next(seed.benefits)
        } else if (shopperIns?.P && estimate.inputs) {
          const benefits = new EligibilityBenefits()
          benefits.deductible = shopperIns.P.deductible ?? 0
          benefits.coinsurance = shopperIns.P.coinsurance ?? 0
          benefits.copayment = shopperIns.P.copayment ?? 0
          benefits.outOfPocketMax = shopperIns.P.outOfPocketMax ?? 0
          this.estimateService.originalBenefits$.next(benefits)
        } else {
          this.estimateService.originalBenefits$.next(null)
        }
        this.estimateService.originalSecondaryBenefits$.next(null)

        if (isNew) {
          // Auto-populate facility from the current selected facility
          const currentFacility = this.facilityService.current$.getValue()
          if (currentFacility?.id) {
            this.estimateService.estimateForm.patchValue({ facilityId: currentFacility.id })
          }
          // Pre-populate patient fields from router navigation state (PIA-1975)
          const state = history.state as Record<string, any>
          if (state?.patientFirstName) {
            this.shopperForm.patchValue({
              patientFirstName: state.patientFirstName ?? '',
              patientLastName: state.patientLastName ?? '',
              patientDOB: state.patientDOB ?? '',
            })
          }
        }
        this.estimateService.setSelectedAppointmentIds([])
        return
      }

      const selectedAppointments = isArray(appointmentsSelected)
        ? appointmentsSelected
        : appointmentsSelected ? [appointmentsSelected] : []
      const selectedAppointmentIds = selectedAppointments.map(a => a.id)
      const shouldUseActiveAppointmentFallback = this.embedded && !selectedAppointmentIds.length

      if (isNew) {
        // In embedded appointment context, fall back to the active appointment when query params are absent.
        this.estimateService.estimateForm.patchValue({
          facilityId: selectedAppointments.length === 1
            ? selectedAppointments[0].locationId
            : shouldUseActiveAppointmentFallback ? activeAppointment?.locationId || null : null,
        })
      }

      if (isNew) {
        // If patient only has one appointment and it wasn't used before, default it
        if (appointments?.length === 1 && !selectedAppointmentIds.includes(appointments[0].id)) {
          this.estimate.appointmentIds = [appointments[0].id]
        } else if (activeAppointment && shouldUseActiveAppointmentFallback) {
          this.estimate.appointmentIds = [activeAppointment.id]
        }
      }
      // Existing estimates without appointment Ids shouldn't auto populate appointments
      // since they were never associated with an appointment in the first place
      const appointmentIds = !isNew && !this.estimate.appointmentIds
        ? []
        : this.estimate.appointmentIds || selectedAppointmentIds
      this.estimateService.setSelectedAppointmentIds(appointmentIds)
    }),
    map(([estimate]) => estimate),
    takeUntil(this.onDestroy$),
  )

  constructor(
    private route: ActivatedRoute,
    private patientDataService: PatientDataService,
    private apiService: ApiService,
    private crudService: CrudService,
    private paramsService: ParamsService,
    private snackBar: MatSnackBar,
    public estimateService: EstimateService,
    private appointmentDataService: AppointmentDataService,
    private router: Router,
  ) {}

  get primaryInsurance$() {
    return this.estimateService.primaryInsurance$
  }

  get templates$() {
    return this.estimateService.templates$
  }

  get claimSearchEnabled$() {
    return this.estimateService.claimSearchEnabled$
  }

  get estimateLoading$() {
    return this.estimateService.estimateLoading$
  }

  get benefitsLoading$() {
    return this.estimateService.benefitsLoading$
  }

  ngOnInit() {

    this.estimateService.pricingNoticeAction$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.estimateSummary?.onCalculate())

    // benefits$ re-emits on insurance change, on initial subscribe (including a component
    // remount, e.g. embedded switching tabs and back), and on notifyBenefitsChanged() (an
    // explicit eligibility edit or a Modify click - see onModify()). Every emission is a
    // candidate to apply: each field is synced from the fresh fetch UNLESS it's been manually
    // typed away from what eligibility last said (see resolveBenefitField) - so a passive
    // remount still picks up a real eligibility change, while a hand-typed value is never
    // silently overwritten by an unrelated notify, a different insurance slot's edit, or a
    // re-pull of the same insurance's eligibility. A genuine LIVE insurance switch on this
    // slot (lastAppliedPrimaryInsuranceId changing) is the one case that always applies
    // regardless of override, since a value typed for the old insurance has no bearing on
    // the new one.
    this.estimateService.benefits$.pipe(
      takeUntil(this.onDestroy$),
      filter(() => !this.shopper),
      tap(({ insuranceId, benefits, previousOriginal, fetchFailed, insuranceRemoved }) => {
        if (fetchFailed || !this.canApplyBenefits()) {
          return
        }

        if (benefits == null && (insuranceId === this.lastAppliedPrimaryInsuranceId || (insuranceId == null && !insuranceRemoved))) {
          return
        }
        const noEligibility = benefits == null && insuranceId != null
        const insuranceChanged = insuranceId !== this.lastAppliedPrimaryInsuranceId && !noEligibility
        this.lastAppliedPrimaryInsuranceId = insuranceId
        // Baseline only moves once applied - see createBenefitsStream in estimate.service.ts.
        this.estimateService.originalBenefits$.next(benefits)

        const inputs = this.estimate.inputs
        const before = {
          deductible: inputs.deductible ?? 0,
          copayment: inputs.copayment ?? 0,
          coinsurance: inputs.coinsurance ?? 0,
          outOfPocketMax: inputs.outOfPocketMax ?? null,
        }
        inputs.deductible = this.resolveBenefitField(
          inputs.deductible, 'deductible', previousOriginal, benefits, 0, insuranceChanged) ?? 0
        inputs.copayment = this.resolveBenefitField(
          inputs.copayment, 'copayment', previousOriginal, benefits, 0, insuranceChanged) ?? 0
        inputs.coinsurance = this.resolveBenefitField(
          inputs.coinsurance, 'coinsurance', previousOriginal, benefits, 0, insuranceChanged) ?? 0
        inputs.outOfPocketMax = this.resolveBenefitField(
          inputs.outOfPocketMax, 'outOfPocketMax', previousOriginal, benefits, null, insuranceChanged)

        if (inputs.deductible !== before.deductible || inputs.copayment !== before.copayment
          || inputs.coinsurance !== before.coinsurance || inputs.outOfPocketMax !== before.outOfPocketMax) {
          this.estimateService.calcAndSaveDisabled.next(false)
          this.estimateService.needsPricing$.next(true)
        }
      }),
    ).subscribe()

    // See comment on the primary benefits$ subscription above - same rationale applies here,
    // plus hasSecondary follows the same override rule as the numeric fields: it tracks
    // "does secondary eligibility data exist" unless the clinician has flipped it manually.
    this.estimateService.secondaryBenefits$.pipe(
      takeUntil(this.onDestroy$),
      filter(() => !this.shopper),
      tap(({ insuranceId, benefits, previousOriginal, fetchFailed, insuranceRemoved }) => {
        if (fetchFailed || !this.canApplyBenefits()) {
          return
        }
        if (benefits == null && (insuranceId === this.lastAppliedSecondaryInsuranceId || (insuranceId == null && !insuranceRemoved))) {
          return
        }
        const noEligibility = benefits == null && insuranceId != null
        const insuranceChanged = insuranceId !== this.lastAppliedSecondaryInsuranceId && !noEligibility
        this.lastAppliedSecondaryInsuranceId = insuranceId
        // Baseline only moves once applied - see createBenefitsStream in estimate.service.ts.
        this.estimateService.originalSecondaryBenefits$.next(benefits)

        const inputs = this.estimate.inputs
        const before = {
          hasSecondary: inputs.hasSecondary,
          secondaryDeductible: inputs.secondaryDeductible ?? 0,
          secondaryCopayment: inputs.secondaryCopayment ?? 0,
          secondaryCoinsurance: inputs.secondaryCoinsurance ?? 0,
          secondaryOutOfPocketMax: inputs.secondaryOutOfPocketMax ?? null,
        }
        inputs.hasSecondary = noEligibility
          ? !!inputs.hasSecondary
          : this.resolveHasSecondaryField(inputs.hasSecondary, previousOriginal, benefits, insuranceChanged)
        inputs.secondaryDeductible = this.resolveBenefitField(
          inputs.secondaryDeductible, 'deductible', previousOriginal, benefits, 0, insuranceChanged) ?? 0
        inputs.secondaryCopayment = this.resolveBenefitField(
          inputs.secondaryCopayment, 'copayment', previousOriginal, benefits, 0, insuranceChanged) ?? 0
        inputs.secondaryCoinsurance = this.resolveBenefitField(
          inputs.secondaryCoinsurance, 'coinsurance', previousOriginal, benefits, 0, insuranceChanged) ?? 0
        inputs.secondaryOutOfPocketMax = this.resolveBenefitField(
          inputs.secondaryOutOfPocketMax, 'outOfPocketMax', previousOriginal, benefits, null, insuranceChanged)

        if (inputs.hasSecondary !== before.hasSecondary || inputs.secondaryDeductible !== before.secondaryDeductible
          || inputs.secondaryCopayment !== before.secondaryCopayment || inputs.secondaryCoinsurance !== before.secondaryCoinsurance
          || inputs.secondaryOutOfPocketMax !== before.secondaryOutOfPocketMax) {
          this.estimateService.calcAndSaveDisabled.next(false)
          this.estimateService.needsPricing$.next(true)
        }
      }),
    ).subscribe()
  }

  // Resolves one field's next value from a fresh benefits fetch, unless the clinician has
  // manually typed it away from what eligibility said last time (currentValue differs from
  // previousOriginal[benefitsKey]) - in which case the current value is returned unchanged.
  // forceApply (a genuine live insurance switch on this slot) bypasses that check entirely,
  // since an override typed for the OLD insurance has no bearing on the newly-selected one.
  // previousOriginal == null means no baseline has ever been recorded for this slot (a legacy
  // estimate saved before originalBenefits existed, or a slot that's never resolved before) -
  // there's nothing to have diverged FROM yet, so the override check is skipped and whatever
  // comes back is applied, establishing a baseline for future calls to diff against. The one
  // exception is newBenefits also being null: with no baseline and no fresh data either,
  // there is nothing to act on, so the current value (whatever it is) is left untouched rather
  // than reset to the default.
  private resolveBenefitField(
    currentValue: number | null | undefined,
    benefitsKey: keyof Pick<EligibilityBenefits, 'deductible' | 'copayment' | 'coinsurance' | 'outOfPocketMax'>,
    previousOriginal: EligibilityBenefits | null,
    newBenefits: EligibilityBenefits | null,
    defaultValue: number | null,
    forceApply: boolean,
  ): number | null {
    if (previousOriginal == null && newBenefits == null && !forceApply) {
      return currentValue ?? defaultValue
    }
    if (!forceApply && previousOriginal != null) {
      const previousValue = previousOriginal[benefitsKey] ?? defaultValue
      if ((currentValue ?? defaultValue) !== previousValue) {
        // Manually overridden since the last sync - leave it alone.
        return currentValue ?? defaultValue
      }
    }
    return newBenefits?.[benefitsKey] ?? defaultValue
  }

  // Same override rule as resolveBenefitField, applied to the hasSecondary toggle: it tracks
  // "does secondary eligibility data exist" unless the clinician has flipped it manually.
  private resolveHasSecondaryField(
    currentValue: boolean | undefined,
    previousOriginal: EligibilityBenefits | null,
    newBenefits: EligibilityBenefits | null,
    forceApply: boolean,
  ): boolean {
    if (previousOriginal == null && newBenefits == null && !forceApply) {
      return !!currentValue
    }
    // Inside this branch previousOriginal != null always means "secondary existed last sync"
    // (there's no such thing as a non-null EligibilityBenefits representing "no secondary"),
    // so the baseline value is always true - overridden only if currentValue has been flipped off.
    if (!forceApply && previousOriginal != null && !currentValue) {
      // Manually overridden since the last sync - leave it alone.
      return false
    }
    return newBenefits != null
  }

  // Live read, not this.estimate.status, so a Modify click (which flips status via
  // estimateService.setEstimate()) is picked up immediately.
  private canApplyBenefits(): boolean {
    return !!this.estimate?.inputs
      && this.estimateService.estimate$.getValue()?.status === this.ESTIMATE_STATUS.DRAFT
  }

  ngOnDestroy() {
    if (this.estimateService.needsPricingNotice) {
      this.estimateService.needsPricingNotice.dismiss()
    }
    this.estimateService.resetState()
    if (this.shopper) {
      this.shopperEstimateService.resetForm()
    }
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  onResetEstimateForm(event: { type: string; estimate: EstimateData }) {
    this.estimateService.resetEstimateFormEvent(event.type, event.estimate)
  }

  onFacilityChanged(id: string | null) {
    this.estimateService.setEstimateFacilityId(id)
  }

  onChargesCleared() {
    this.estimateService.clearEstimateCharges()
  }

  onTemplateSaved(id: string) {
    this.estimateService.setEstimateTemplateId(id)
  }

  onAddChargesRequested(payload: { reset?: boolean }) {
    this.estimateService.addChargesByAppointments(payload.reset)
  }

  onDirty() {
    this.estimateService.markDirty()
  }

  onShopperInsuranceChanged(event: ShopperInsuranceChangeEvent) {
    if (!this.estimate) {
      return
    }
    this.estimate.shopperInsurances = event.insurances
    this.estimate.eligibilityId = event.eligibilityId
    this.estimate.inputs = {
      ...this.estimate.inputs,
      deductible: event.inputs.deductible ?? 0,
      coinsurance: event.inputs.coinsurance ?? 0,
      copayment: event.inputs.copayment ?? 0,
      outOfPocketMax: event.inputs.outOfPocketMax ?? null,
      insuranceType: event.inputs.insuranceType,
      estimateType: this.estimate.inputs?.estimateType ?? 'patientpal',
      insuranceCarrier: event.inputs.insuranceCarrier,
    }
    const originalBenefits = new EligibilityBenefits()
    originalBenefits.deductible = event.inputs.deductible ?? 0
    originalBenefits.coinsurance = event.inputs.coinsurance ?? 0
    originalBenefits.copayment = event.inputs.copayment ?? 0
    originalBenefits.outOfPocketMax = event.inputs.outOfPocketMax ?? 0
    this.estimateService.originalBenefits$.next(originalBenefits)
    this.estimateService.calcAndSaveDisabled.next(false)
    this.estimateService.needsPricing$.next(true)
  }

  openEligibility() {
    if (this.patient?.id) {
      window.open(`/patient/${this.patient.id}/eligibility`, '_blank')
    }
  }

  onEstimateDeleted() {
    if (this.shopper) {
      if (this.embedded) {
        // The embedded widget has no shopper-estimates route to navigate back to,
        // so reset in place instead of the no-op navigate() the routed console context uses.
        this.shopperEstimateService.resetForm()
        this.standardEstimateId$.next('new')
      } else {
        this.router.navigate(['/shopper-estimates'])
      }
      this.snackBar.open('Estimate deleted successfully', '', { duration: 3000 })
      return
    }

    this.patientDataService.refreshDocuments$.next()
    this.estimateService.refreshAppointments$.next()
    const patientId = this.estimateService.patientId$.getValue()
    if (patientId && !this.embedded) {
      this.router.navigate(['/patient', patientId])
    } else {
      this.appointmentDataService.appointmentUpdated$.next()
      this.standardEstimateId$.next('new')
    }

    this.snackBar.open('Estimate deleted successfully', '', { duration: 3000 })
  }
}
